import type { UseTuningSession } from '../hooks/useTuningSession';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, WarningCard, InfoCard } from '../components/Cards';

interface LimbDrivenData {
  /** Where on the limb is the cord clamped? */
  cordPosition?: 'tip' | 'mid' | 'pocket';
  /** Estimated launcher up-percentage from draw-board observation. */
  guidancePercent?: number;
  /** Has fletching contact been verified (foot powder, vane streaks)? */
  contactClean?: boolean;
}

interface Props { tuning: UseTuningSession }

/**
 * Step 6a — Limb-Driven Rest Setup
 *
 * Setup procedure:
 *   1. Mount rest on the riser (5/16 cap-head screw, standard Berger hole)
 *   2. Run the actuation cord from the launcher arm to a clamp on the BOTTOM limb
 *      (or top, depending on rest model)
 *   3. Position clamp toward limb TIP to make rest come up sooner / stay up longer
 *      Position clamp toward limb POCKET to delay rest rise and shorten its time up
 *   4. Verify launcher supports the arrow for 50-70% of the power stroke
 */
export function RestLimbDrivenStep({ tuning }: Props) {
  const data = (tuning.session.stepData['rest_limb_driven'] as LimbDrivenData | undefined) ?? {};
  const update = (patch: Partial<LimbDrivenData>) =>
    tuning.updateStepData<LimbDrivenData>('rest_limb_driven', { ...data, ...patch });

  const inGuidanceRange = data.guidancePercent !== undefined
    && data.guidancePercent >= 50 && data.guidancePercent <= 70;
  const guidanceLow = data.guidancePercent !== undefined && data.guidancePercent < 50;
  const guidanceHigh = data.guidancePercent !== undefined && data.guidancePercent > 70;

  return (
    <>
      <GoalCard title="What we're trying to achieve">
        The launcher should be UP and supporting the arrow for 50-70% of the power
        stroke. Less than 50% = the arrow loses support before it reaches launch speed.
        More than 70% = increased risk of fletching contact with the launcher arm.
      </GoalCard>

      <Card padding="lg">
        <h3 className="font-semibold mb-2">Install procedure</h3>
        <ol className="list-decimal list-inside space-y-1.5 text-[13px] text-zinc-300 leading-relaxed">
          <li>
            Mount the rest to the riser using the included 5/16" cap-head screw through the
            Berger hole. Lightly snug; we'll torque after centerShot is dialed.
          </li>
          <li>
            Bend the actuation cord under the buss cable (so it doesn't interfere with the
            cable's free movement).
          </li>
          <li>
            Loop the cord around the limb at the clamp position and tighten the clamp.
            Cord should be TAUT at brace (the rest is held DOWN by spring tension at brace
            and pulled UP as the bow is drawn).
          </li>
          <li>
            Verify by drawing slowly — the launcher should rise smoothly and reach full-up
            well before full draw.
          </li>
        </ol>
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-3">Cord clamp position on the limb</h3>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          The limb flexes more near the tip and less near the pocket. Moving the cord
          clamp toward the tip = MORE travel = rest rises sooner. Toward the pocket = LESS
          travel = rest rises later.
        </p>
        <div className="space-y-2">
          <Choice
            label="Near the limb TIP (early rise, long stays up)"
            selected={data.cordPosition === 'tip'}
            onClick={() => update({ cordPosition: 'tip' })}
          />
          <Choice
            label="Mid-limb (balanced — recommended starting position)"
            selected={data.cordPosition === 'mid'}
            onClick={() => update({ cordPosition: 'mid' })}
          />
          <Choice
            label="Near the limb POCKET (late rise, short stays up)"
            selected={data.cordPosition === 'pocket'}
            onClick={() => update({ cordPosition: 'pocket' })}
          />
        </div>
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-2">Guidance check</h3>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          On a draw board, OR with a friend watching as you slow-draw: roughly what
          percentage of the power stroke is the launcher in the UP position? Aim for
          50-70%.
        </p>
        <Field label="% of power stroke launcher is UP">
          <input
            type="number"
            min={0}
            max={100}
            step={5}
            value={data.guidancePercent ?? ''}
            onChange={(e) => update({ guidancePercent: parseFloatOrUndef(e.target.value) })}
            placeholder="50-70"
            className={inputClass}
          />
        </Field>
        {data.guidancePercent !== undefined && (
          <div className="mt-3">
            {inGuidanceRange ? (
              <Status ok>{data.guidancePercent}% — in the sweet spot.</Status>
            ) : guidanceLow ? (
              <Status>
                Only {data.guidancePercent}% — rest is coming up too late. Move cord clamp TOWARD limb tip
                (more cord travel = earlier rise).
              </Status>
            ) : guidanceHigh ? (
              <Status>
                {data.guidancePercent}% — rest is up too long. Move cord clamp TOWARD limb pocket
                (less travel = later rise). Watch for fletching contact.
              </Status>
            ) : null}
          </div>
        )}
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-2">Fletching contact check</h3>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          Spray the rest launcher arm and cables with foot powder (Gold Bond, Dr. Scholl's).
          Shoot one arrow. Look for streak marks where vanes scraped — those mean contact.
        </p>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!data.contactClean}
            onChange={(e) => update({ contactClean: e.target.checked })}
            className="mt-1 w-4 h-4 accent-ember-500"
          />
          <span className="text-sm">No fletching contact — clean powder pattern</span>
        </label>
      </Card>

      <TipCard title="If you see fletching contact">
        <ol className="list-decimal list-inside space-y-1 text-zinc-300">
          <li>First try rotating the nock 60° (or a half-rotation for 2-vane setups) and re-test.</li>
          <li>If contact persists, your launcher is up too long — move the cord clamp toward limb pocket.</li>
          <li>Re-check guidance % after the move — should still land in 50-70% window.</li>
          <li>If you can't eliminate contact, try a stiffer launcher arm or longer fletch.</li>
        </ol>
      </TipCard>

      <InfoCard title="Why limb-driven is fail-safe">
        <p>
          If the actuation cord breaks during a shot or between shots, a limb-driven rest
          stays in the UP position (spring inside the launcher pushes it up by default —
          the cord pulls it DOWN at brace). Your arrow still fires near its zeroed POI.
          Cable-driven rests, conversely, fall DOWN on a cord break — usually a lost arrow.
        </p>
      </InfoCard>

      <WarningCard title="Safety reminder">
        Use only the cord material that came with your rest (typically 24-lb braided
        D-loop or specialized rest cord). Generic string or paracord stretches under
        repeated cycle and will throw your tune off within a week. Inspect the cord
        every 200 shots.
      </WarningCard>
    </>
  );
}

const inputClass =
  'w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ember-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="text-[11px] text-zinc-500 block mb-1">{label}</label>{children}</div>;
}

function Choice({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={
        'w-full text-left rounded-md border px-3 py-2.5 text-sm transition ' +
        (selected
          ? 'border-ember-500 bg-ember-500/10 text-ember-500 font-semibold'
          : 'border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700')
      }
    >
      {label}
    </button>
  );
}

function Status({ children, ok }: { children: React.ReactNode; ok?: boolean }) {
  return (
    <div className={`text-sm leading-relaxed p-2.5 rounded border ${ok ? 'border-emerald-700/40 bg-emerald-950/20 text-emerald-300' : 'border-amber-700/40 bg-amber-950/20 text-amber-300'}`}>
      {children}
    </div>
  );
}

function parseFloatOrUndef(s: string): number | undefined {
  if (s === '') return undefined;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : undefined;
}
